import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { PERMISSIONS } from "@/constants/permissions"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useState } from "react"
import { catalog } from "./api"
import {
  ArticleImportError,
  parseArticlePasteRows,
  parseArticleWorkbookRows,
  type ArticleImportErrorCode,
  type ArticleImportRow,
} from "./articleImport"
import { openCompanyArticleHarvest } from "./harvestBridge"
import {
  PILOT_FRAMEWORKS,
  pilotRequests,
  type PilotFramework,
} from "./pilotRequestsApi"
import { RequestOutcomePanel } from "./RequestOutcomePanel"

import { RequestMatchingPanel } from "./RequestMatchingPanel"

const importErrorKeys: Record<ArticleImportErrorCode, TranslationKeys> = {
  empty: "CI_ARTICLE_IMPORT_EMPTY",
  column: "CI_ARTICLE_IMPORT_COLUMN",
  limit: "CI_ARTICLE_IMPORT_LIMIT",
  worksheet: "CI_ARTICLE_IMPORT_WORKSHEET",
  table: "CI_ARTICLE_IMPORT_TABLE",
  size: "CI_ARTICLE_IMPORT_SIZE",
}

export function PilotRequestPanel({ companyId }: { companyId: string }) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const client = useQueryClient()
  const key = [
    "company-index",
    user?.organization,
    "research-requests",
    companyId,
  ]
  const [title, setTitle] = useState("")
  const [domain, setDomain] = useState("")
  const [codes, setCodes] = useState("")
  const [frameworks, setFrameworks] = useState<PilotFramework[]>([])
  const [importError, setImportError] = useState("")
  const [importPreview, setImportPreview] = useState<ArticleImportRow[]>([])
  const [harvestError, setHarvestError] = useState(false)
  const importMessage = (error: unknown) =>
    t(
      error instanceof ArticleImportError
        ? importErrorKeys[error.code]
        : "CI_ARTICLE_EXCEL_ERROR",
    )
  const launchHarvest = async (partNumbers: string[]) => {
    try {
      const company = await catalog.company(companyId)
      await openCompanyArticleHarvest(companyId, company.website, partNumbers)
      setHarvestError(false)
    } catch {
      setHarvestError(true)
    }
  }
  const canUpload = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)
  const rows = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => pilotRequests.list(companyId, pageParam),
    initialPageParam: 0,
    getNextPageParam: (page, pages) =>
      page.length === 30 ? pages.length * 30 : undefined,
    enabled: hasPermission(PERMISSIONS.MC_DECLARATIONS_VIEW),
  })
  let pastedRows: ArticleImportRow[] = []
  let pasteError = ""
  try {
    if (codes.trim()) pastedRows = parseArticlePasteRows(codes)
  } catch (error) {
    pasteError = importMessage(error)
  }
  const parts = pastedRows.map((row) => row.itemNumber)
  const save = useMutation({
    mutationFn: async (harvest: boolean) => {
      const request = await pilotRequests.create(companyId, {
        title: title.trim(),
        scope: {
          issuerDomain: domain.trim(),
          requirements: parts.map((partNumber) => ({ partNumber, frameworks })),
        },
      })
      return { request, harvest }
    },
    onSuccess: async ({ request, harvest }) => {
      void client.invalidateQueries({ queryKey: key })
      setTitle("")
      setCodes("")
      setImportPreview([])
      if (harvest)
        await launchHarvest(
          request.scope.requirements.map((item) => item.partNumber),
        )
    },
  })
  const canSubmit =
    !save.isPending &&
    !!title.trim() &&
    !!domain.trim() &&
    parts.length > 0 &&
    parts.length <= 1000 &&
    frameworks.length > 0
  return (
    <section className="space-y-4 rounded-xl border border-border p-5">
      <h2 className="font-semibold">{t("CI_REQUESTS")}</h2>
      <p className="text-sm text-muted-foreground">{t("CI_REQUEST_HINT")}</p>
      {canUpload && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit)
              save.mutate(
                (e.nativeEvent as SubmitEvent).submitter?.getAttribute(
                  "data-harvest",
                ) === "true",
              )
          }}
        >
          <label className="space-y-1 block">
            <span>{t("CI_REQUEST_TITLE")}</span>
            <Input
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="space-y-1 block">
            <span>{t("CI_ISSUER_DOMAIN")}</span>
            <Input
              required
              maxLength={253}
              placeholder="harting.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
          </label>
          <label className="space-y-1 block">
            <span>{t("CI_CODES")}</span>
            <Textarea
              required
              value={codes}
              onChange={(e) => {
                setCodes(e.target.value)
                setImportPreview([])
              }}
            />
          </label>
          <label className="space-y-1 block">
            <span>{t("CI_ARTICLE_EXCEL")}</span>
            <Input
              type="file"
              accept=".xlsx,.xlsm,.xls"
              aria-label={t("CI_ARTICLE_EXCEL")}
              onChange={async (event) => {
                const file = event.target.files?.[0]
                if (!file) return
                try {
                  if (file.size > 5 * 1024 * 1024)
                    throw new ArticleImportError("size")
                  const imported = parseArticleWorkbookRows(
                    await file.arrayBuffer(),
                  )
                  setCodes(imported.map((row) => row.itemNumber).join("\n"))
                  setImportPreview(imported)
                  setImportError("")
                } catch (error) {
                  setImportError(importMessage(error))
                }
              }}
            />
          </label>
          {importError && <p role="alert">{importError}</p>}
          {pasteError && <p role="alert">{pasteError}</p>}
          <p className="text-xs text-muted-foreground">
            {parts.length} {t("CI_REQUEST_ARTICLES")}
          </p>
          {(importPreview.length ? importPreview : pastedRows).some(
            (row) => row.masterData || row.description,
          ) && (
            <div
              className="max-h-40 overflow-auto rounded border p-2 text-xs"
              aria-label={t("CI_ARTICLE_PREVIEW")}
            >
              <strong>{t("CI_ARTICLE_PREVIEW")}</strong>
              {(importPreview.length ? importPreview : pastedRows)
                .slice(0, 5)
                .map((row) => (
                  <p key={row.itemNumber}>
                    {row.itemNumber} · {row.masterData} · {row.description}
                  </p>
                ))}
            </div>
          )}
          <fieldset>
            <legend className="mb-2">{t("CI_REQUEST_FRAMEWORKS")}</legend>
            <div className="flex flex-wrap gap-4">
              {PILOT_FRAMEWORKS.map((f) => (
                <label key={f} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={frameworks.includes(f)}
                    onChange={(e) =>
                      setFrameworks((current) =>
                        e.target.checked
                          ? [...current, f]
                          : current.filter((x) => x !== f),
                      )
                    }
                  />
                  {f === "other"
                    ? t("MC_QP_FRAMEWORK_OTHER")
                    : f.toUpperCase().replaceAll("_", " ")}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={!canSubmit}>
              {t("CI_REQUEST_CREATE")}
            </Button>
            <Button
              type="submit"
              variant="outline"
              data-harvest="true"
              disabled={!canSubmit}
            >
              {t("CI_REQUEST_CREATE_HARVEST")}
            </Button>
          </div>
          {parts.length > 1000 && <p role="alert">{t("CI_REQUEST_LIMIT")}</p>}
          {save.isError && <p role="alert">{t("CI_ERROR")}</p>}
          {harvestError && <p role="alert">{t("CI_ARTICLE_HARVEST_ERROR")}</p>}
        </form>
      )}
      {rows.isError && (
        <p role="alert">
          {t("CI_ERROR")}{" "}
          <Button onClick={() => void rows.refetch()}>{t("CI_RETRY")}</Button>
        </p>
      )}
      {rows.data?.pages.flat().map((request) => (
        <details key={request.id} className="rounded-lg border p-3">
          <summary className="cursor-pointer font-medium">
            {request.title} · {request.scope.requirements.length}{" "}
            {t("CI_REQUEST_ARTICLES")}
          </summary>
          <p className="mt-2 text-sm text-muted-foreground">
            {request.scope.issuerDomain}
          </p>
          <ul className="mt-2 max-h-64 overflow-auto text-sm">
            {request.scope.requirements.map((r, i) => (
              <li key={i}>
                {r.partNumber} · {r.frameworks.join(", ")}
              </li>
            ))}
          </ul>
          <RequestMatchingPanel request={request} />
          {canUpload && (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                void launchHarvest(
                  request.scope.requirements.map((item) => item.partNumber),
                )
              }
            >
              {t("CI_ARTICLE_HARVEST")}
            </Button>
          )}
          <RequestOutcomePanel request={request} />
        </details>
      ))}
      {rows.hasNextPage && (
        <Button
          variant="outline"
          disabled={rows.isFetchingNextPage}
          onClick={() => void rows.fetchNextPage()}
        >
          {t("CI_MORE")}
        </Button>
      )}
    </section>
  )
}
